"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { toast } from "sonner";

export default function LoginPage() {
    const [isLoading, setIsLoading] = useState(false);
    const router = useRouter();

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setIsLoading(true);

        const formData = new FormData(event.currentTarget);
        const username = formData.get("username") as string;
        const password = formData.get("password") as string;

        try {
            const response = await fetch("/api/v1/core/login", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ username, password }),
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error("Credenciales inválidas");
                }
                throw new Error("Error al iniciar sesión");
            }

            toast.success("Sesión iniciada correctamente");
            router.push("/dashboard");
            router.refresh(); // Refresh to update server components/middleware state if any
        } catch (error) {
            const message = error instanceof Error ? error.message : "Ha ocurrido un error";
            if (message !== "Credenciales inválidas") {
                console.error(error);
            }
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <div className="w-full h-screen grid lg:grid-cols-2">
            {/* Left Side - Visual */}
            <div className="hidden lg:flex flex-col justify-between p-10 bg-muted relative overflow-hidden">
                {/* Background Image & Overlay */}
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: "url('/hero-newsroom.jpg')" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />

                {/* Content over image */}
                <div className="relative z-10">
                    <Link href="/" className="flex items-center gap-2 text-white font-bold text-xl">
                        <img src="/logo_m4b.png" alt="M4B Logo" className="h-8 brightness-0 invert" />
                    </Link>
                </div>

                <div className="relative z-10 text-white max-w-lg">
                    <h2 className="text-4xl font-bold mb-4 leading-tight">
                        El futuro del periodismo digital está aquí.
                    </h2>
                    <p className="text-lg text-white/80">
                        Gestiona tus publicaciones, analiza el impacto y conecta con tu audiencia desde una única plataforma inteligente.
                    </p>
                </div>
            </div>

            {/* Right Side - Form */}
            <div className="flex items-center justify-center p-8 bg-background">
                <div className="w-full max-w-md space-y-6">
                    <div className="text-center space-y-2">
                        <h1 className="text-2xl font-bold tracking-tight">Bienvenido de nuevo</h1>
                        <p className="text-sm text-muted-foreground">
                            Introduce tus credenciales para acceder al panel.
                        </p>
                    </div>

                    <form onSubmit={onSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="username">Usuario o email</Label>
                            <Input id="username" name="username" type="text" placeholder="usuario o email" required disabled={isLoading} />
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="password">Contraseña</Label>
                                <Link href="#" className="text-sm font-medium text-primary hover:underline">
                                    ¿Olvidaste tu contraseña?
                                </Link>
                            </div>

                            <Input id="password" name="password" type="password" required disabled={isLoading} />
                        </div>

                        <Button type="submit" className="w-full" disabled={isLoading}>
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Iniciando sesión...
                                </>
                            ) : (
                                "Iniciar Sesión"
                            )}
                        </Button>
                    </form>

                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-background px-2 text-muted-foreground">O</span>
                        </div>
                    </div>

                    <div className="text-center space-y-2">
                        <p className="text-sm text-muted-foreground">
                            ¿Aún no tienes cuenta?
                        </p>
                        <Button variant="outline" className="w-full" asChild>
                            <Link href="/precios">
                                Prueba gratis 15 días con nuestros planes
                            </Link>
                        </Button>
                    </div>

                    <p className="px-8 text-center text-sm text-muted-foreground">
                        Al continuar, aceptas nuestros{" "}
                        <Link href="/terminos" className="underline underline-offset-4 hover:text-primary">
                            Términos de servicio
                        </Link>{" "}
                        y{" "}
                        <Link href="/privacidad" className="underline underline-offset-4 hover:text-primary">
                            Política de privacidad
                        </Link>
                        .
                    </p>
                </div>
            </div>
        </div>
    );
}
